import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { User } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { loginWithEmail } from "../services/authentication";
import assetPath from "../utils/assetPath";
import ROUTES from "../utils/ROUTES";
import { ArrowRight, ShieldCheck, Lock, User as UserIcon } from "lucide-react";

const imgLogo = assetPath("/logo.png");

const schema = z.object({
  email: z.email("Please enter a valid email address"),
  password: z.string().min(1, "Please enter your password"),
});

type LoginFormData = z.infer<typeof schema>;

interface LoginProps {
  onSuccess?: (user: User) => void;
  onSignUpClick?: () => void;
}

const Login = ({ onSuccess }: LoginProps) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(schema) });

  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ email, password }: LoginFormData) => loginWithEmail(email, password),
    onSuccess,
  });

  const [errorDismissed, setErrorDismissed] = useState(false);

  useEffect(() => {
    setErrorDismissed(false);
  }, [error]);

  const { onChange: onEmailChange, ...emailRest } = register("email");
  const { onChange: onPasswordChange, ...passwordRest } = register("password");

  const dismissError = () => {
    if (error) setErrorDismissed(true);
  };

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center px-4 pt-8 pb-40 bg-brand-primary"
    >
      {/* Logo */}
      <img
        src={imgLogo}
        alt="The Coconut Tree"
        className="mb-10 h-[85px] w-[249px] object-cover"
      />

      {/* Login card */}
      <div
        className="relative w-full max-w-[452px] flex flex-col gap-10 rounded-[32px] border border-white/40 p-[41px] backdrop-blur-[6px] bg-surface-card"
        style={{
          boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.25)",
        }}
      >
        {/* Heading */}
        <div className="flex flex-col gap-1 text-center">
          <h1
            className="text-2xl uppercase tracking-[0.48px] text-content-heading"
            style={{ fontFamily: '"Bebas Neue", sans-serif' }}
          >
            Administrator Sign In
          </h1>
          <p
            className="text-xs font-medium text-content-label"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            Authorized access only. Log in to manage loyalty programs.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit((data) => mutate(data))}
          noValidate
          className="flex flex-col gap-6"
        >
          {/* Username */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="px-1 text-xs font-medium text-content-label"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Username
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <UserIcon className="h-[13px] w-[13px]" />
              </span>
              <input
                id="email"
                type="email"
                placeholder="Enter your username"
                className="font-montserrat w-full rounded-lg py-[18px] pl-10 pr-4 text-base font-medium outline-none placeholder:text-[#c2c7c9] bg-surface-input text-content-label"
                {...emailRest}
                onChange={(e) => { onEmailChange(e); dismissError(); }}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between px-1">
              <label
                htmlFor="password"
                className="text-xs font-medium text-content-label"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Password
              </label>
              <button
                type="button"
                onClick={() => navigate(ROUTES.FORGOT_ACCESS)}
                className="text-xs font-medium hover:underline text-brand-teal"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Forgot access?
              </button>
            </div>
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <Lock className="h-[17px] w-[13px]" />
              </span>
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                className="font-montserrat w-full rounded-lg py-[18px] pl-10 pr-4 text-base font-medium outline-none placeholder:text-[#c2c7c9] bg-surface-input text-content-label"
                {...passwordRest}
                onChange={(e) => { onPasswordChange(e); dismissError(); }}
              />
            </div>
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          {error && !errorDismissed && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
              {(error as Error).message}
            </p>
          )}

          {/* Sign In button */}
          <button
            type="submit"
            disabled={isPending}
            className="flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 font-semibold text-white transition disabled:opacity-60"
            style={{
              background: "linear-gradient(136.97deg, #011b21 0%, #173036 100%)",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)",
            }}
          >
            {isPending ? "Signing in…" : "Sign In"}
            {!isPending && (
              <ArrowRight className="h-[13px] w-[13px]" />
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="border-t pt-8" style={{ borderColor: "rgba(194,199,201,0.2)" }}>
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="h-[13px] w-[13px]" />
            <span
              className="text-xs font-medium text-content-label"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              End-to-end encrypted session
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
